import React, { useMemo } from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';
import {
  calculateAnimationsStyle,
  mergeAnimationStyle,
} from '../animation/calculate-animation';
import {
  calculateFadeInOpacity,
  calculateFadeOutOpacity,
} from '../video/calculate-fade';
import {
  calculateTemplateEffect,
  mergeTemplateStyle,
} from '../template/template-effects';
import { SolidItem } from './solid-item-type';
import { defaultTextShadow, TextShadow } from '../text/text-item-type';
import {nativeCallLaneBarAt} from '@waka/compat/call-lane-bar';

const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const SolidLayer = ({
  item,
  zIndex,
}: {
  item: SolidItem;
  zIndex?: number;
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const opacity = useMemo(() => {
    const inOpacity = calculateFadeInOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeInDurationInSeconds: item.fadeInDurationInSeconds,
      framesPerSecond: fps,
    });
    const outOpacity = calculateFadeOutOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
    });
    return inOpacity * outOpacity * item.opacity;
  }, [
    item.visualFadeWindow,
    item.fadeInDurationInSeconds,
    fps,
    frame,
    item.opacity,
    item.durationInFrames,
    item.fadeOutDurationInSeconds,
  ]);

  const shadow = useMemo<TextShadow>(
    () => ({ ...defaultTextShadow, ...(item.shadow ?? {}) }),
    [item.shadow],
  );

  const strokeWidth = item.strokeWidth ?? 0;
  const strokeColor =
    item.strokeVisible === false
      ? 'transparent'
      : (item.strokeColor ?? 'transparent');
  const fillColor = item.colorVisible === false ? 'transparent' : item.color;
  const callBar = item.wakaCallLaneBar
    ? nativeCallLaneBarAt(item.wakaCallLaneBar, frame, item.height, item.width, item.color)
    : null;

  const style: React.CSSProperties = useMemo(() => {
    const dropShadowFilter =
      !item.boxShadow && shadow.visible && item.shadowVisible !== false
        ? `drop-shadow(${shadow.offsetX}px ${shadow.offsetY}px ${shadow.blur}px ${hexToRgba(shadow.color, shadow.opacity)})`
        : undefined;

    const borderValue =
      strokeWidth > 0 && strokeColor !== 'transparent'
        ? `${strokeWidth}px solid ${strokeColor}`
        : undefined;

    return {
      backgroundColor: item.colorVisible === false ? 'transparent' : callBar ? callBar.color : fillColor,
      position: 'absolute',
      left: item.left,
      top: callBar ? item.top + (item.height - callBar.height) / 2 : item.top,
      width: item.width,
      height: callBar ? callBar.height : item.height,
      opacity,
      borderRadius: item.borderRadius,
      transform: `rotate(${item.rotation}deg)`,
      zIndex,
      isolation: zIndex !== undefined ? 'isolate' : undefined,
      boxSizing: 'border-box',
      border: borderValue,
      filter: dropShadowFilter,
      boxShadow: item.boxShadow,
    };
  }, [
    fillColor,
    item.height,
    item.left,
    item.top,
    item.width,
    opacity,
    item.borderRadius,
    item.rotation,
    zIndex,
    strokeWidth,
    strokeColor,
    shadow,
    item.shadowVisible,
    item.boxShadow,
    callBar,
    item.colorVisible,
  ]);

  // Animation 스타일 계산
  const animationStyle = useMemo(() => {
    const hasAnimations = item.animations && item.animations.length > 0;
    const hasPositionKeyframes =
      item.positionKeyframes && item.positionKeyframes.length > 0;
    if (!hasAnimations && !hasPositionKeyframes) {
      return {};
    }
    return calculateAnimationsStyle({
      animations: item.animations ?? [],
      currentFrame: frame,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
      positionKeyframes: item.positionKeyframes,
    });
  }, [
    item.animations,
    item.positionKeyframes,
    frame,
    fps,
    item.durationInFrames,
  ]);

  const templateEffect = useMemo(
    () =>
      calculateTemplateEffect({
        item,
        localFrame: frame,
        fps,
      }),
    [item, frame, fps],
  );

  // style에 animation 적용
  const finalStyle = useMemo(() => {
    const animatedStyle = mergeAnimationStyle(style, animationStyle);
    if (!templateEffect) return animatedStyle;
    return mergeTemplateStyle(animatedStyle, templateEffect.outerStyle);
  }, [style, animationStyle, templateEffect]);

  const header = item.templateBackground?.header;

  return (
    <div style={finalStyle}>
      {header ? (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: header.height ?? 0,
            backgroundColor: header.backgroundColor,
            borderBottom:
              header.dividerColor && header.dividerHeight
                ? `${header.dividerHeight}px solid ${header.dividerColor}`
                : undefined,
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </div>
  );
};
